import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, filter, tap} from 'rxjs';
import {AuthService} from './auth.service';
import {ActivationEnd, NavigationStart, Router, Routes} from '@angular/router';
import {ROLE} from '../utils/enums/role';
import {getStored, setStored} from '../utils/other/utilities';

@Injectable({
    providedIn: 'root'
})
export class RouteService {

    public routeChecked: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private readonly validRoutes: string[] = [];
    private pageReloaded: { started?: boolean, ended?: boolean, url?: string } = {};
    private readonly VALID_UNAUTH_URLS = ['/registration', '/privacy-policy', '/help-desk'];

    private readonly _authService: AuthService = inject(AuthService);
    private readonly _router: Router = inject(Router);

    private _reloadSubWatcher = true;

    constructor() {
        this.extractRoutes(this._router.config[0].children);
        this.initRouteWatcher();
    }

    private initRouteWatcher(): void {
        this._router.events.pipe(
            filter(evt => evt instanceof NavigationStart || evt instanceof ActivationEnd),
            tap(evt => {
                if (evt instanceof NavigationStart) {
                    this.pageReloaded.url = evt.url;
                    this.pageReloaded.started = true;
                } else this.pageReloaded.ended = true;
            })
        ).subscribe(evt => {
            if (!(this.pageReloaded.started && this.pageReloaded.ended && evt instanceof ActivationEnd)) {
                if (this._reloadSubWatcher) {
                    this._reloadSubWatcher = false;
                } else {
                    setStored('FNP_RELOADED', '1');
                }
                this.pageReloaded = {};
            }
        });
    }

    private extractRoutes(routes: Routes): void {
        routes.forEach(route => {
            this.validRoutes.push(route.path);
            if (route.children && route?.children.length > 0) {
                this.extractRoutes(route.children);
            }
        });
    }

    public checkRoute(): boolean {
        const role: ROLE = this._authService.userRole;
        const currentUrl: string = this._router.getCurrentNavigation()
            .extractedUrl
            .toString()
            .split('?')[0];

        if ((this.routeChecked.getValue() || this.isValidReloaded()) && this.isRouteValid(currentUrl.replace('/', ''))) {
            this.routeChecked.next(false);
            setStored('FNP_RELOADED', '0');
            return true;
        } else {
            this.routeChecked.next(true);
            return (role === ROLE.ADMIN && currentUrl === '/users-management')
                || (role === ROLE.USER && currentUrl === '/fnp')
                || this.isUnAuthValidUrl(currentUrl);
        }
    }

    public isValidReloaded(): boolean {
        return getStored('FNP_RELOADED') === '1';
    }

    private isUnAuthValidUrl(url: string): boolean {
        return this.VALID_UNAUTH_URLS.includes(url);
    }

    public navigateToDefault(): Promise<boolean> {
        return this._router.navigate([this.getDefaultUrlForCurrentRole()]);
    }

    public getDefaultUrlForCurrentRole(): string {
        return this._authService.userRole === ROLE.ADMIN ? '/users-management' : '/fnp';
    }

    public isRouteValid(url: string): boolean {
        return this.validRoutes.findIndex(route => route === url) !== -1;
    }

    public get currentRoute(): string {
        return this._router.url;
    }
}
